"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  eyebrow: "HOTELS OPTIMIZED",
  value: 150,
  suffix: "+",
  caption: "Properties transformed through data-driven revenue strategies.",
  fill: "hsl(43 46% 55% / 0.15)"
}, {
  eyebrow: "REVENUE INCREASE",
  value: 35,
  suffix: "%",
  caption: "Average growth in RevPAR achieved for our partner hotels.",
  fill: "hsl(43 46% 55% / 0.15)"
}, {
  eyebrow: "OTA LISTINGS",
  value: 500,
  suffix: "+",
  caption: "Optimized listings driving higher visibility and direct bookings.",
  fill: "hsl(43 46% 55% / 0.15)"
}, {
  eyebrow: "TECH INTEGRATIONS",
  value: 85,
  suffix: "",
  caption: "Seamless hospitality tech stack implementations for efficiency.",
  fill: "hsl(43 46% 55% / 0.15)"
}];
function formatNumber(n: number, target: number): string {
  if (target >= 1000000) {
    return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (target >= 1000) {
    return Math.floor(n).toLocaleString("en-US");
  }
  return Math.floor(n).toString();
}
function AnimatedNumber({
  target,
  suffix,
  start
}: {
  target: number;
  suffix: string;
  start: boolean;
}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!start) return;
    const duration = 1600;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [start, target]);
  return <span className="tabular-nums">
      {formatNumber(value, target)}
      {suffix}
    </span>;
}
export default function EfficiencyStats() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: scribbleRef,
    isVisible: scribbleVisible
  } = useRevealOnScroll<SVGSVGElement>({
    threshold: 0.4
  });
  return <section data-section-id="1312" id="efficiencystats" className="relative overflow-hidden py-28 md:py-40 bg-background text-foreground">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
        <span className="font-default leading-none" style={{
        color: "var(--primary)",
        opacity: 0.03,
        fontSize: "clamp(20rem, 40vw, 36rem)",
        fontWeight: 700
      }}>
          %
        </span>
      </div>

      <div ref={sectionRef} className="relative mx-auto max-w-[1280px] px-6 md:px-12" style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(16px)",
      transition: "opacity 600ms cubic-bezier(0.22, 1, 0.36, 1), transform 600ms cubic-bezier(0.22, 1, 0.36, 1)"
    }}>
        <div className="mx-auto max-w-[720px] text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="font-default text-[11px] sm:text-xs md:text-[13px] font-medium uppercase" style={{
            color: "var(--secondary)",
            letterSpacing: "0.18em"
          }}>
              HOTELREVBABA · REVENUE IMPACT
            </span>
            <span aria-hidden="true" className="inline-block h-2 w-2 rounded-lg" style={{
            backgroundColor: "var(--secondary)"
          }} />
          </div>

          <h2 className="font-default mt-6 font-bold" style={{
          color: "var(--primary)",
          fontSize: "clamp(2rem, 4.4vw, 3.75rem)",
          letterSpacing: "-0.02em",
          lineHeight: 1.08
        }}>
            Proven results.{" "}
            <span className="relative inline-block">
              <span className="relative z-10 italic" style={{
              color: "var(--primary)"
            }}>
                Measurable
              </span>
              <svg ref={scribbleRef} aria-hidden="true" viewBox="0 0 120 70" className="absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2" style={{
              width: "130%",
              height: "140%",
              transform: "translate(-50%, -50%) rotate(-4deg)",
              overflow: "visible"
            }}>
                <path d="M 60 8 C 90 8, 112 22, 112 35 C 112 50, 88 62, 60 62 C 30 62, 8 50, 8 35 C 8 20, 32 8, 60 8 Z" fill="none" stroke="var(--secondary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="320" strokeDashoffset={scribbleVisible ? 0 : 320} style={{
                transition: "stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1)"
              }} />
              </svg>
            </span>{" "}
            growth.
          </h2>

          <p className="font-default mt-5 mx-auto max-w-[560px] text-base md:text-lg" style={{
          color: "var(--foreground)",
          opacity: 0.75,
          lineHeight: 1.6
        }}>
            Empowering hotel owners with data-driven strategies to maximize revenue and streamline operations.
          </p>
        </div>

        <div className="relative mt-16 md:mt-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, i) => <div key={i} data-index={i} className="relative" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)",
            transition: `opacity 600ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 80}ms, transform 600ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 80}ms`
          }}>
                <div className="relative rounded-lg p-7 md:p-8 h-full flex flex-col shadow-md" style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)"
            }}>
                  <div className="font-default text-[11px] font-bold uppercase" style={{
                color: "var(--secondary)",
                letterSpacing: "0.18em"
              }}>
                    {stat.eyebrow}
                  </div>

                  <div className="font-default mt-4 font-bold leading-none" style={{
                color: "var(--primary)",
                fontSize: "clamp(3rem, 7vw, 4rem)",
                letterSpacing: "-0.025em"
              }}>
                    <AnimatedNumber target={stat.value} suffix={stat.suffix} start={isVisible} />
                  </div>

                  <p className="font-default mt-4 text-sm" style={{
                color: "var(--foreground)",
                opacity: 0.85,
                lineHeight: 1.5
              }}>
                    {stat.caption}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}